// ClientStampsCard — Phase S1 sidebar presentation card +
// Phase S10 narrative-metadata completion.
//
// Pure render. Receives the parent-loaded `client` snapshot. No fetch,
// no dispatch, no selector, no internal state, no orchestration. Per
// CLAUDE.md "Presentation Primitive Rules": prop-driven, selector-
// free, dispatch-free, ownership-agnostic. Sidebar peer of
// [[ClientNotesCard]].
//
// Phase S10 — narrative-region restoration. Completes the design's
// audit-metadata grouping (design/clientDetail.html lines 677-693 —
// "Створено / Оновлено / Власник запису") whose third semantic slot
// was previously absent from React. The Owner row is essential
// dossier context — when the viewer is the record owner it reads
// "Ви"; when they are viewing a shared client it names the record's
// actual owner, communicating the ownership relationship without
// any permissions UX (which is foundation-class per R8 §5 O-1 and
// explicitly forbidden by the S10 brief §7).
//
// Backend-truth mapping (verified):
//   * client.owner          — ClientDetailSerializer.owner,
//     UserShortSerializer payload {id, public_id, handle, email,
//     fullname, user_image}. Required, non-null on Client (FK with
//     on_delete=CASCADE — see backend/app/apps/clients/models.py:99).
//   * client.is_owner       — ClientDetailSerializer computed flag
//     (request.user.id === obj.owner_id — see serializers.py:94-96).
//     This is the same flag that gates the S7 destructive action.
//
// No inferred semantics, no fake aggregates, no derived hints.
// Section is topology-neutral — the data ships with the parent's
// existing client-core payload; no new fetch path is introduced.

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const yyyy = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${dd}.${mo}.${yyyy}`;
};

// Resolve the "Власник запису" display string per design semantic:
//   * the viewing user IS the owner          → "Ви"
//   * the viewing user is a share recipient  → owner.fullname
// UserShortSerializer.fullname is computed server-side as
// first+last → handle → email, so it's always non-empty in practice.
// Defensive fallback chain mirrors that order; null when no owner
// payload arrived at all (which would be a backend-shape change).
const ownerDisplayName = (client) => {
  if (!client) return null;
  if (client.is_owner) return "Ви";
  const owner = client.owner;
  if (!owner) return null;
  if (typeof owner.fullname === "string" && owner.fullname.trim().length > 0) {
    return owner.fullname;
  }
  if (typeof owner.handle === "string" && owner.handle.trim().length > 0) {
    return owner.handle;
  }
  if (typeof owner.email === "string" && owner.email.trim().length > 0) {
    return owner.email;
  }
  return null;
};

function ClientStampsCard({ client }) {
  if (!client) return null;

  const ownerLabel = ownerDisplayName(client);

  if (!client.created_at && !client.updated_at && !ownerLabel) return null;

  return (
    <article className="cd-card cd-card--minimal">
      <dl className="feature-client-detail__stamps">
        {client.created_at ? (
          <div className="feature-client-detail__stamps-row">
            <dt>Створено</dt>
            <dd>{formatDate(client.created_at)}</dd>
          </div>
        ) : null}
        {client.updated_at ? (
          <div className="feature-client-detail__stamps-row">
            <dt>Оновлено</dt>
            <dd>{formatDate(client.updated_at)}</dd>
          </div>
        ) : null}
        {ownerLabel ? (
          <div className="feature-client-detail__stamps-row">
            <dt>Власник запису</dt>
            <dd>{ownerLabel}</dd>
          </div>
        ) : null}
      </dl>
    </article>
  );
}

export default ClientStampsCard;
